import React, { useEffect, useState } from 'react';
import { Polyline, Marker, Popup, Polygon, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import * as turf from '@turf/turf';
import { message } from 'antd';
import { annotationRouteService } from '@iso/services';
import { StyleToggle, escapeHtml } from './AnnotationShared';

// ─── Colour palettes ──────────────────────────────────────────────────────────

const ROUTE_COLORS = ['#EF4444', '#EAB308', '#3B82F6', '#F97316', '#16A34A', '#6B7280'];
const ZONE_COLORS  = ['#EF4444', '#EAB308', '#16A34A', '#3B82F6', '#F97316', '#8B5CF6', '#6B7280'];

// ─── Style → dashArray helper ─────────────────────────────────────────────────

export const getDashArray = (style) => {
  if (style === 'solid')  return null;
  if (style === 'dotted') return '2 6';
  return '8 5'; // dashed (default)
};

// ─── Shared popup UI pieces ───────────────────────────────────────────────────

const swatch = (hex, selected, onClick) => (
  <button
    key={hex}
    onClick={onClick}
    style={{
      width: 36, height: 36, borderRadius: 8, background: hex,
      border: selected ? '3px solid #111827' : '2px solid rgba(0,0,0,0.12)',
      cursor: 'pointer', padding: 0, flexShrink: 0,
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
    }}
  />
);


const PropertiesPopup = ({
  type, labelInput, onLabelChange, styleInput, onStyleChange, colorInput, onColorChange, colors,
  labelSize, onLabelSizeChange, labelRotation, onLabelRotationChange, labelColor, onLabelColorChange,
  onSave, onDelete, deleteLabel = 'Delete',
}) => (
  <div style={{ minWidth: 250 }}>
    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: '#111827' }}>Properties</div>

    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, fontWeight: 500 }}>Type</div>
      <div style={{ fontSize: 14, color: '#3B82F6' }}>{type}</div>
    </div>

    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 5 }}>Label</div>
      <input
        value={labelInput}
        onChange={e => onLabelChange(e.target.value)}
        placeholder="Enter name…"
        style={{
          width: '100%', padding: '9px 12px',
          border: '1px solid #f3f4f6', borderRadius: 10,
          fontSize: 14, background: '#f9fafb', boxSizing: 'border-box',
        }}
      />
    </div>

    {onLabelSizeChange && (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 5 }}>
          Label Size: {labelSize}px
        </div>
        <input type="range" min={8} max={40} value={labelSize}
          onChange={e => onLabelSizeChange(Number(e.target.value))}
          style={{ width: '100%', accentColor: '#111827' }}
        />
      </div>
    )}

    {onLabelRotationChange && (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 5 }}>
          Label Rotation: {labelRotation}°
        </div>
        <input type="range" min={0} max={359} value={labelRotation}
          onChange={e => onLabelRotationChange(Number(e.target.value))}
          style={{ width: '100%', accentColor: '#111827' }}
        />
      </div>
    )}

    {onLabelColorChange && (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Label Color</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {LABEL_COLORS.map(hex => swatch(hex, labelColor === hex, () => onLabelColorChange(hex)))}
        </div>
      </div>
    )}

    <StyleToggle value={styleInput} onChange={onStyleChange} />

    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Color</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {colors.map(hex => swatch(hex, colorInput === hex, () => onColorChange(hex)))}
      </div>
    </div>

    <button onClick={onSave} style={{
      width: '100%', padding: '9px 0', marginBottom: 8,
      background: '#0d3825', color: 'white', border: 'none',
      borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    }}>
      Save Changes
    </button>

    <button onClick={onDelete} style={{
      width: '100%', padding: '11px 0',
      background: '#DC2626', color: 'white', border: 'none',
      borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
      </svg>
      {deleteLabel}
    </button>
  </div>
);

// ─── Leaflet helpers ──────────────────────────────────────────────────────────

const LABEL_COLORS = ['#EF4444', '#EAB308', '#F97316', '#16A34A', '#3B82F6', '#8B5CF6', '#374151'];

const makeZoneLabelIcon = (label, size = 13, rotation = 0, color = '#1f2937') => L.divIcon({
  html: `<div style="pointer-events:none;display:inline-block;background:rgba(255,255,255,0.92);border:1.5px solid rgba(0,0,0,0.2);border-radius:4px;padding:3px 9px;font-size:${size}px;font-weight:700;color:${escapeHtml(color)};white-space:nowrap;transform:translate(-50%,-50%) rotate(${Number(rotation)}deg);box-shadow:0 1px 4px rgba(0,0,0,0.15)">${escapeHtml(label)}</div>`,
  className: '',
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

const waypointIcon = L.divIcon({
  html: '<div style="width:12px;height:12px;background:white;border:2.5px solid #3B82F6;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>',
  iconSize: [12, 12], iconAnchor: [6, 6], className: '',
});

const zonePointIcon = L.divIcon({
  html: '<div style="width:10px;height:10px;background:white;border:2px solid #16A34A;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>',
  iconSize: [10, 10], iconAnchor: [5, 5], className: '',
});

const buildArrowMarkers = (from, to, color) => {
  const line = turf.lineString([[from.lng, from.lat], [to.lng, to.lat]]);
  const length = turf.length(line, { units: 'meters' });
  const markers = [];
  let dist = 40;
  while (dist < length) {
    const pt = turf.along(line, dist, { units: 'meters' });
    const bearing = turf.bearing(turf.point([from.lng, from.lat]), turf.point([to.lng, to.lat]));
    const [lng, lat] = pt.geometry.coordinates;
    markers.push({ lat, lng, icon: L.divIcon({
      html: `<div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-bottom:12px solid ${escapeHtml(color)};transform:rotate(${bearing}deg);transform-origin:center bottom"></div>`,
      iconSize: [12, 12], iconAnchor: [6, 6], className: '',
    }) });
    dist += 80;
  }
  return markers;
};

// ─── ActiveRouteTool ──────────────────────────────────────────────────────────

export const ActiveRouteTool = ({ isActive, cursor = 'crosshair' }) => {
  useEffect(() => {
    if (!isActive) return;
    const el = document.querySelector('.leaflet-container');
    if (!el) return;
    el.style.cursor = cursor;
    return () => { el.style.cursor = ''; };
  }, [isActive, cursor]);
  return null;
};

// ─── RouteInProgress ──────────────────────────────────────────────────────────

export const RouteInProgress = ({ waypoints, color, style = 'dashed' }) => {
  if (!waypoints.length) return null;
  const positions = waypoints.map(w => [w.lat, w.lng]);
  const arrows = [];
  for (let i = 0; i < waypoints.length - 1; i++) {
    buildArrowMarkers(waypoints[i], waypoints[i + 1], color).forEach(m => arrows.push(m));
  }
  return (
    <>
      <Polyline positions={positions} color={color} weight={3} dashArray={getDashArray(style)} />
      {arrows.map((m, i) => <Marker key={i} position={[m.lat, m.lng]} icon={m.icon} />)}
      {waypoints.map((w, i) => <Marker key={`wp-${i}`} position={[w.lat, w.lng]} icon={waypointIcon} />)}
    </>
  );
};

// ─── ZoneInProgress ───────────────────────────────────────────────────────────

export const ZoneInProgress = ({ waypoints, color = '#16A34A', style = 'dashed' }) => {
  if (waypoints.length < 2) return null;
  const positions = waypoints.map(w => [w.lat, w.lng]);
  return (
    <>
      <Polygon positions={positions} color={color} fillColor={color} fillOpacity={0.15} weight={2} dashArray={getDashArray(style)} />
      {waypoints.map((w, i) => <Marker key={`zp-${i}`} position={[w.lat, w.lng]} icon={zonePointIcon} />)}
    </>
  );
};

// ─── ZoneItem ─────────────────────────────────────────────────────────────────
// Single interactive polygon — clicking anywhere in/on zone opens edit popup.
// SVG overlay pane (z-index 400) is below marker pane (z-index 600), so
// annotation marker HTML divs always intercept clicks before this polygon.

const ZoneItem = ({ zone, eraserMode, onEraseZone, onUpdate, onDelete }) => {
  const [labelInput,    setLabelInput]    = useState(zone.label       || '');
  const [colorInput,    setColorInput]    = useState(zone.color       || '#16A34A');
  const [styleInput,    setStyleInput]    = useState(zone.style       || 'dashed');
  const [labelSize,     setLabelSize]     = useState(Number(zone.label_size)     || 13);
  const [labelRotation, setLabelRotation] = useState(Number(zone.label_rotation) || 0);
  const [labelColor,    setLabelColor]    = useState(zone.label_color || '#1f2937');

  useEffect(() => {
    setLabelInput(zone.label       || '');
    setColorInput(zone.color       || '#16A34A');
    setStyleInput(zone.style       || 'dashed');
    setLabelSize(Number(zone.label_size)     || 13);
    setLabelRotation(Number(zone.label_rotation) || 0);
    setLabelColor(zone.label_color || '#1f2937');
  }, [zone.label, zone.color, zone.style, zone.label_size, zone.label_rotation, zone.label_color]);

  let waypoints = [];
  try { waypoints = typeof zone.waypoints === 'string' ? JSON.parse(zone.waypoints) : zone.waypoints; } catch { return null; }
  if (!waypoints.length) return null;

  const positions = waypoints.map(w => [w.lat, w.lng]);
  const displayColor = eraserMode ? '#EF4444' : (zone.color || '#16A34A');

  const avgLat = waypoints.reduce((s, w) => s + w.lat, 0) / waypoints.length;
  const avgLng = waypoints.reduce((s, w) => s + w.lng, 0) / waypoints.length;

  const handleSave = () => {
    const updated = { ...zone, label: labelInput, color: colorInput, style: styleInput, label_size: labelSize, label_rotation: labelRotation, label_color: labelColor };
    annotationRouteService.storeOrUpdate({ annotation_route: updated }, zone.id)
      .then(() => { if (onUpdate) onUpdate(updated); })
      .catch(err => { console.error('Failed to update zone:', err); message.error('Failed to save changes'); });
  };

  const handleDelete = () => {
    annotationRouteService.destroy(zone.id)
      .then(() => { if (onDelete) onDelete(zone.id); })
      .catch(err => { console.error('Failed to delete zone:', err); message.error('Failed to delete zone'); });
  };

  return (
    <>
      <Polygon
        positions={positions}
        color={displayColor}
        fillColor={zone.color || '#16A34A'}
        fillOpacity={eraserMode ? 0.35 : 0.15}
        weight={eraserMode ? 3 : 2}
        dashArray={getDashArray(zone.style)}
        onClick={() => { if (eraserMode && onEraseZone) onEraseZone(zone.id); }}
      >
        {!eraserMode && (
          <Popup>
            <PropertiesPopup
              type="Zone"
              labelInput={labelInput}
              onLabelChange={setLabelInput}
              styleInput={styleInput}
              onStyleChange={setStyleInput}
              colorInput={colorInput}
              onColorChange={setColorInput}
              colors={ZONE_COLORS}
              labelSize={labelSize}
              onLabelSizeChange={setLabelSize}
              labelRotation={labelRotation}
              onLabelRotationChange={setLabelRotation}
              labelColor={labelColor}
              onLabelColorChange={setLabelColor}
              onSave={handleSave}
              onDelete={handleDelete}
              deleteLabel="Delete Zone"
            />
          </Popup>
        )}
      </Polygon>
      {zone.label && !eraserMode && (
        <Marker
          position={[avgLat, avgLng]}
          icon={makeZoneLabelIcon(zone.label, labelSize, labelRotation, labelColor)}
          zIndexOffset={-200}
        />
      )}
    </>
  );
};

// ─── SavedZones ───────────────────────────────────────────────────────────────

export const SavedZones = ({ zones, eraserMode, onEraseZone, onUpdate, onDelete }) => (
  <>
    {zones.map(zone => (
      <ZoneItem key={zone.id} zone={zone} eraserMode={eraserMode}
        onEraseZone={onEraseZone} onUpdate={onUpdate} onDelete={onDelete} />
    ))}
  </>
);

// ─── RouteItem ────────────────────────────────────────────────────────────────

const RouteItem = ({ route, onDelete, onUpdate, eraserMode }) => {
  const [labelInput, setLabelInput] = useState(route.label || '');
  const [colorInput, setColorInput] = useState(route.color || '#3B82F6');
  const [styleInput, setStyleInput] = useState(route.style || 'dashed');

  useEffect(() => {
    setLabelInput(route.label || '');
    setColorInput(route.color || '#3B82F6');
    setStyleInput(route.style || 'dashed');
  }, [route.label, route.color, route.style]);

  let waypoints = [];
  try { waypoints = typeof route.waypoints === 'string' ? JSON.parse(route.waypoints) : route.waypoints; } catch { return null; }
  if (!waypoints.length) return null;

  const positions = waypoints.map(w => [w.lat, w.lng]);
  const color = eraserMode ? '#EF4444' : (route.color || '#3B82F6');
  const arrows = [];
  for (let i = 0; i < waypoints.length - 1; i++) {
    buildArrowMarkers(waypoints[i], waypoints[i + 1], color).forEach(m => arrows.push(m));
  }

  const handleDelete = () => {
    annotationRouteService.destroy(route.id)
      .then(() => { if (onDelete) onDelete(route.id); })
      .catch(err => { console.error('Failed to delete route:', err); message.error('Failed to delete route'); });
  };

  const handleSave = () => {
    const updated = { ...route, label: labelInput, color: colorInput, style: styleInput };
    annotationRouteService.storeOrUpdate({ annotation_route: updated }, route.id)
      .then(() => { if (onUpdate) onUpdate(updated); })
      .catch(err => { console.error('Failed to update route:', err); message.error('Failed to save changes'); });
  };

  return (
    <React.Fragment>
      {/* Thin visible line — non-interactive */}
      <Polyline positions={positions} color={color} weight={eraserMode ? 5 : 3}
        dashArray={getDashArray(route.style)} interactive={false} />
      {/* Wide hit-area — opacity=0.01 keeps stroke painted for SVG pointer-events.
          showMeasurements={false} because it sits on the same coordinates as the
          visible line, and two label sets stacked on each other read as blur. */}
      <Polyline positions={positions} color={color} weight={16} opacity={0.01}
        showMeasurements={false}
        onClick={() => { if (eraserMode) handleDelete(); }}
      >
        {!eraserMode && (
          <Popup>
            <PropertiesPopup
              type="Route"
              labelInput={labelInput}
              onLabelChange={setLabelInput}
              styleInput={styleInput}
              onStyleChange={setStyleInput}
              colorInput={colorInput}
              onColorChange={setColorInput}
              colors={ROUTE_COLORS}
              onSave={handleSave}
              onDelete={handleDelete}
              deleteLabel="Delete Route"
            />
          </Popup>
        )}
      </Polyline>
      {arrows.map((m, i) => (
        <Marker key={`${route.id}-a-${i}`} position={[m.lat, m.lng]} icon={m.icon} />
      ))}
    </React.Fragment>
  );
};

// ─── AnnotationRouteMarkers ───────────────────────────────────────────────────

const AnnotationRouteMarkers = ({ routes, onDelete, onUpdate, eraserMode }) => (
  <>
    {routes.filter(r => !r.type || r.type === 'route').map(route => (
      <RouteItem key={route.id} route={route}
        onDelete={onDelete} onUpdate={onUpdate} eraserMode={eraserMode} />
    ))}
  </>
);

export default AnnotationRouteMarkers;
